import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import GroupAddIcon from "@mui/icons-material/GroupAdd";
import GroupRemoveIcon from "@mui/icons-material/GroupRemove";
import MenuBookIcon from "@mui/icons-material/MenuBook";
import {
  Box,
  Card,
  CardContent,
  CircularProgress,
  Grid,
  Typography
} from "@mui/material";
import { useKeycloak } from "@react-keycloak/web";
import { Roles } from "@services/accessManagerApi/models/Roles";
import { t } from "i18next";
import React from "react";
import { sourceCard } from "../../../theme/styles";

export interface SourcesProps {
  loading: boolean;
  roles: Roles[];
  onDelete: (id: number) => void;
  onOpenRoleModal: (id: number) => void;
  onAssignRoleToUser: (id: number) => Promise<void>;
  onUnassignRoleToUser: (id: number) => Promise<void>;
}

interface IconContainerProps {
  active: boolean;
  onClick: () => void;
}

const DeleteIconContainer: React.FC<IconContainerProps> = ({
  active,
  onClick
}) => (
  <Box
    sx={{
      p: 0.5,
      borderRadius: "50%",
      width: 32,
      height: 32,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      "&:hover": { cursor: "pointer" },
      backgroundColor: active ? "white" : "transparent"
    }}
    onClick={onClick}
  >
    <DeleteOutlineIcon
      sx={{ color: active ? "error.main" : "red", fontSize: 25 }}
    />
  </Box>
);

const AssignIconContainer: React.FC<IconContainerProps> = ({
  active,
  onClick
}) => (
  <Box
    sx={{
      p: 0.5,
      borderRadius: "50%",
      width: 32,
      height: 32,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      "&:hover": { cursor: "pointer" },
      backgroundColor: active ? "white" : "transparent"
    }}
    onClick={onClick}
  >
    <GroupAddIcon color="primary" sx={{ fontSize: 25 }} />
  </Box>
);

const UnassignIconContainer: React.FC<IconContainerProps> = ({
  active,
  onClick
}) => (
  <Box
    sx={{
      p: 0.5,
      borderRadius: "50%",
      width: 32,
      height: 32,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      "&:hover": { cursor: "pointer" },
      backgroundColor: active ? "white" : "transparent"
    }}
    onClick={onClick}
  >
    <GroupRemoveIcon
      sx={{ color: active ? "error.main" : "red", fontSize: 25 }}
    />
  </Box>
);

const UserIcon: React.FC<{ active: boolean }> = ({ active }) => (
  <Box
    sx={{
      display: "flex",
      justifyContent: "center",
      alignItems: "center",
      p: 1,
      my: 2,
      height: "60px"
    }}
  >
    <MenuBookIcon
      sx={{ fontSize: 70, color: active ? "white" : "customColors.syntetis" }}
    />
  </Box>
);

const RoleName: React.FC<{ name: string; active: boolean }> = ({
  name,
  active
}) => (
  <Typography
    variant="h6"
    sx={{
      fontSize: 14,
      fontWeight: 400,
      wordBreak: "break-word",
      hyphens: "auto",
      textAlign: "center",
      mt: 2,
      color: active ? "white" : "#626262"
    }}
    title={name}
  >
    {name}
  </Typography>
);

export const MastersCard: React.FC<SourcesProps> = ({
  loading,
  roles,
  onDelete,
  onAssignRoleToUser,
  onUnassignRoleToUser
}) => {
  const { keycloak } = useKeycloak();

  if (loading) {
    return (
      <Grid item>
        <CircularProgress size={24} sx={{ mr: 2 }} />
      </Grid>
    );
  }

  if (!loading && roles.length === 0) {
    return (
      <Grid item>
        <Typography component="h2" sx={{ fontSize: 20, fontWeight: 500 }}>
          {t("administration.masters.no_masters_uploaded")}
        </Typography>
      </Grid>
    );
  }

  return (
    <>
      {roles.map((role) => {
        const isRoleActive = keycloak.hasRealmRole(role.name);
        return (
          <Grid item xs={12} sm={6} md={3} key={role.id}>
            <Card sx={sourceCard(isRoleActive)}>
              <CardContent
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  p: 2,
                  position: "relative"
                }}
              >
                <Grid
                  container
                  mb={1}
                  justifyContent="space-between"
                  alignItems="center"
                >
                  <Grid item>
                    {isRoleActive && (
                      <UnassignIconContainer
                        active={true}
                        onClick={() => onUnassignRoleToUser(role.id)}
                      />
                    )}
                    {!isRoleActive && (
                      <AssignIconContainer
                        active={true}
                        onClick={() => onAssignRoleToUser(role.id)}
                      />
                    )}
                  </Grid>
                  <Grid item>
                    <DeleteIconContainer
                      active={isRoleActive}
                      onClick={() => onDelete(role.id)}
                    />
                  </Grid>
                </Grid>

                <UserIcon active={isRoleActive} />

                <RoleName name={role.name} active={isRoleActive} />
              </CardContent>
            </Card>
          </Grid>
        );
      })}
    </>
  );
};
